import {
  type Cluster,
  type ClusterStats,
  DefaultRenderer,
  type Marker,
} from '@googlemaps/markerclusterer';
import { lighten, type Theme } from '@mui/material';
import type { useGeocoder } from '@/views/Community/views/CommunityListings/CommunityListings.helpers';
import { DEFAULT_LOCALE_IDS } from '../../../constants';
import { matchesGeocoderResult } from '../../../utils/location';
import type { GeocodeLocation } from '../../LocationSearchBar/LocationAutocomplete';
import { GroupAwareCluster } from './GroupAwareCluster';

type GetLocationCallback = () => GeocodeLocation | null;
type GeocodeCallback = ReturnType<typeof useGeocoder>['geocode'];

export class ClusterRenderer extends DefaultRenderer {
  private readonly theme: Theme;

  private readonly getLocation: GetLocationCallback;

  private readonly geocode: GeocodeCallback;

  constructor(
    theme: Theme,
    getCurrentLocation: GetLocationCallback,
    geocode: GeocodeCallback,
  ) {
    super();

    this.theme = theme;
    this.getLocation = getCurrentLocation;
    this.geocode = geocode;
  }

  private isDefaultSearch() {
    const selectedLocation = this.getLocation();
    return selectedLocation && DEFAULT_LOCALE_IDS.includes(selectedLocation.id);
  }

  /**
   * Convert a cluster into a map marker.
   *
   * @see https://googlemaps.github.io/js-markerclusterer/interfaces/MarkerClustererOptions.html#renderer
   */
  public render(
    initCluster: Cluster,
    stats: ClusterStats,
    map: google.maps.Map,
  ): Marker {
    const cluster = new GroupAwareCluster({
      position: initCluster.position,
      markers: initCluster.markers,
    });

    const marker = super.render(cluster, stats, map);

    if (!(marker instanceof google.maps.marker.AdvancedMarkerElement)) {
      return marker;
    }

    const svg = marker.element.querySelector('svg');

    if (!(svg instanceof SVGElement)) {
      return marker;
    }

    const text = svg.querySelector('text');
    const circle = svg.querySelector('circle');

    if (text) {
      text.style.fontWeight = `${this.theme.typography.fontWeightSemiBold}`;
    }

    if (circle) {
      circle.style.opacity = '0.85';
    }

    svg.style.fill = 'transparent';
    const selectedLocation = this.getLocation();
    const isVectorMap = map.getRenderingType() === 'VECTOR';
    const defaultSearch = this.isDefaultSearch();

    if (!selectedLocation || !isVectorMap || defaultSearch) {
      svg.style.fill = this.theme.palette.secondary.main;
      return marker;
    }

    this.geocode({ location: cluster.position })
      .then((res) => {
        if (matchesGeocoderResult(selectedLocation, res)) {
          svg.style.fill = this.theme.palette.secondary.main;
        } else {
          svg.style.fill = lighten(this.theme.palette.secondary.main, 0.4);
          if (text) {
            text.style.fill = this.theme.palette.secondary.dark;
          }
        }
      })
      .catch(() => {
        svg.style.fill = this.theme.palette.secondary.main;
      });

    return marker;
  }
}
